/*
 * Header scroll behaviour, driven by two attributes on <html> that the
 * header section styles react to:
 *   data-header="collapsed"  — past COLLAPSE_AT the wordmark tier folds away
 *                              and the mini logo appears; it unfolds only
 *                              back near the top (EXPAND_AT), so scroll
 *                              anchoring, shifting the page by the 48px it
 *                              loses or regains, cannot make it oscillate.
 *   data-scrolldir="down|up" — once the page content has reached the top of
 *                              the window, the whole header slides out on
 *                              scroll down and back in on scroll up. Any
 *                              earlier, it would uncover the blank page above
 *                              the content.
 */
(() => {
  const COLLAPSE_AT = 120;
  const EXPAND_AT = 8;
  const DEAD_ZONE = 2;
  const html = document.documentElement;
  const main = document.getElementById('main');
  // How far the content has scrolled past the top of the window. Measured against the content, not
  // the document: the scroll anchoring that follows the folding tier moves both alike, so it never
  // reads as a change of direction.
  const contentScroll = () => window.scrollY - main.offsetTop;
  let lastScroll = contentScroll();
  let dir = null;
  let ticking = false;

  const update = () => {
    ticking = false;
    const y = window.scrollY;
    const scroll = contentScroll();
    const collapsed = html.hasAttribute('data-header');
    if (!collapsed && y > COLLAPSE_AT) html.setAttribute('data-header', 'collapsed');
    else if (collapsed && y < EXPAND_AT) html.removeAttribute('data-header');

    const delta = scroll - lastScroll;
    lastScroll = scroll;
    if (scroll < 0) {
      if (dir !== null) {
        html.removeAttribute('data-scrolldir');
        dir = null;
      }
    } else if (Math.abs(delta) >= DEAD_ZONE) {
      const next = delta > 0 ? 'down' : 'up';
      if (next !== dir) {
        html.setAttribute('data-scrolldir', next);
        dir = next;
      }
    }
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    },
    { passive: true }
  );
  // A focused element inside the header (keyboard navigation) brings it back.
  document.addEventListener('focusin', (event) => {
    if (event.target.closest('.header') && dir === 'down') {
      html.setAttribute('data-scrolldir', 'up');
      dir = 'up';
    }
  });
  update();
})();

/* Full-width image panels: hover intent, keyboard focus and Theme Editor reloads. */
(() => {
  const controllers = new WeakMap();
  const init = (nav) => {
    controllers.get(nav)?.();
    const items = Array.from(nav.querySelectorAll('[data-mega]'));
    if (!items.length) return;
    const controller = new AbortController();
    const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: controller.signal });
    const desktop = matchMedia('(min-width: 1101px)'); // the header's drawer breakpoint (sections/header.liquid)
    let current = null;
    let openTimer;
    let closeTimer;
    let previousScrollStyle;
    const cancelTimers = () => { clearTimeout(openTimer); clearTimeout(closeTimer); };
    const setOpen = (item) => {
      if (current === item) return;
      if (current) {
        current.removeAttribute('data-open');
        current.querySelector('[aria-controls]').setAttribute('aria-expanded', 'false');
        current.querySelector('[data-mega-panel]').inert = true;
      }
      if (!current && item) {
        const html = document.documentElement;
        previousScrollStyle = { overflow: html.style.overflow, paddingRight: html.style.paddingRight };
        const scrollbar = window.innerWidth - html.clientWidth;
        if (scrollbar > 0) html.style.paddingRight = `${parseFloat(getComputedStyle(html).paddingRight) + scrollbar}px`;
        html.style.overflow = 'hidden';
      } else if (current && !item) {
        Object.assign(document.documentElement.style, previousScrollStyle);
      }
      current = item;
      nav.toggleAttribute('data-mega-visible', Boolean(item));
      if (item) nav.style.setProperty('--mega-height', `${item.querySelector('[data-mega-panel]').offsetHeight}px`);
      if (item) {
        item.setAttribute('data-open', '');
        item.querySelector('[aria-controls]').setAttribute('aria-expanded', 'true');
        item.querySelector('[data-mega-panel]').inert = false;
      }
    };
    const close = () => { cancelTimers(); setOpen(null); };
    nav.setAttribute('data-js', '');
    items.forEach((item) => {
      const panel = item.querySelector('[data-mega-panel]');
      panel.inert = true;
      listen(item, 'mouseenter', () => {
        cancelTimers();
        if (desktop.matches) openTimer = setTimeout(() => setOpen(item), 60);
      });
      listen(item, 'mouseleave', () => {
        cancelTimers();
        closeTimer = setTimeout(() => setOpen(null), 140);
      });
      listen(item, 'focusin', () => {
        cancelTimers();
        if (desktop.matches) setOpen(item);
      });
      listen(item, 'focusout', (event) => {
        if (!item.contains(event.relatedTarget)) close();
      });
      listen(panel, 'click', (event) => {
        if (event.target.closest('a')) close();
      });
    });
    listen(document, 'keydown', (event) => {
      if (event.key !== 'Escape' || !current) return;
      event.preventDefault();
      // Focus first: focusin must finish before closing, otherwise it reopens the panel.
      current.querySelector('[aria-controls]').focus({ preventScroll: true });
      close();
    });
    listen(document, 'pointerdown', (event) => {
      if (current && !current.contains(event.target)) close();
    });
    listen(desktop, 'change', close);
    const dispose = () => { close(); controller.abort(); nav.removeAttribute('data-js'); };
    controllers.set(nav, dispose);
    listen(document, 'shopify:section:unload', (event) => {
      if (event.target.contains(nav)) dispose();
    });
  };
  const initWithin = (root) => root.querySelectorAll('[data-mega-nav]').forEach(init);
  initWithin(document);
  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
})();
